import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Plus, Trash2, Sparkles, X, Send } from "lucide-react";
import api from "../lib/api";
import { Loader, Pill } from "../components/common";

const WEEKDAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Programs() {
  const [programs, setPrograms] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [apply, setApply] = useState(null);   // { program, customer_id, start }
  const [builder, setBuilder] = useState(null); // { name, description, weeks, days:{wd:val} }

  const load = () => api.get("/programs").then(({ data }) => setPrograms(data));
  useEffect(() => {
    load();
    api.get("/coach/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/workouts").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);
  if (!programs) return <Loader />;

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/programs/seed-hypertrophy");
      toast.success(data.added > 0 ? `Added ${data.added} hypertrophy program(s).` : "Hypertrophy programs already loaded.");
      await load();
      api.get("/workouts").then(({ data }) => setTemplates(data));
    } catch (e) { toast.error("Could not load hypertrophy programs."); }
    finally { setSeeding(false); }
  };

  const doApply = async () => {
    if (!apply.customer_id) return toast.error("Select a customer.");
    try {
      const { data } = await api.post(`/programs/${apply.program.id}/apply`, { customer_id: apply.customer_id, start_date: apply.start });
      toast.success(`Applied ${data.workouts} workout(s) + ${data.rest_days} rest day(s).`);
      setApply(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply program."); }
  };

  const removeProgram = async (p) => {
    if (!window.confirm(`Delete program "${p.name}"? Already-scheduled workouts are not affected.`)) return;
    try { await api.delete(`/programs/${p.id}`); toast.success("Program deleted."); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
  };

  const saveBuilder = async () => {
    if (!builder.name) return toast.error("Name your program.");
    const pattern = {};
    Object.entries(builder.days).forEach(([wd, val]) => {
      if (val === "rest") pattern[wd] = { type: "rest" };
      else if (val) pattern[wd] = { type: "workout", workout_id: val };
    });
    if (Object.keys(pattern).filter((k) => pattern[k].type === "workout").length === 0) return toast.error("Add at least one workout day.");
    try {
      await api.post("/programs", { name: builder.name, description: builder.description, weeks: Number(builder.weeks) || 1, weekly_pattern: pattern });
      toast.success("Program saved!");
      setBuilder(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save program."); }
  };

  const hasSystem = programs.some((p) => p.system);

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-5xl">Programs</h1><p className="text-secondary mt-1">Multi-week training blocks you can apply to any client in one click.</p></div>
        <div className="flex items-center gap-2">
          <button data-testid="prog-seed" onClick={seed} disabled={seeding} className="px-4 py-2.5 border border-white/20 rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:border-brand hover:text-brand disabled:opacity-60"><Sparkles className="w-4 h-4" /> {seeding ? "Loading…" : "Load Hypertrophy Programs"}</button>
          <button data-testid="prog-new" onClick={() => setBuilder({ name: "", description: "", weeks: 4, days: {} })} className="px-4 py-2.5 bg-brand text-white rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:bg-brand-hover"><Plus className="w-4 h-4" /> New Program</button>
        </div>
      </div>

      {!hasSystem && (
        <div className="card-surface rounded-xl p-4 mt-6 text-sm text-secondary">Tip: click <span className="text-brand font-semibold">Load Hypertrophy Programs</span> to add ready-made 4-week and 8-week hypertrophy plans.</div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {programs.map((p) => (
          <div key={p.id} data-testid={`prog-card-${p.id}`} className="card-surface rounded-xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-brand/15 flex items-center justify-center flex-shrink-0"><CalendarRange className="w-5 h-5 text-brand" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><h3 className="font-semibold truncate">{p.name}</h3>{p.system ? <Pill>Built-in</Pill> : <Pill tone="warning">Custom</Pill>}</div>
                <p className="text-xs text-tertiary mt-0.5">{p.weeks} weeks</p>
              </div>
            </div>
            <p className="text-sm text-secondary mt-3 line-clamp-3">{p.description}</p>
            <div className="flex gap-2 mt-4">
              <button data-testid={`prog-apply-${p.id}`} onClick={() => setApply({ program: p, customer_id: "", start: todayStr() })} className="flex-1 py-2 bg-brand text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1"><Send className="w-4 h-4" /> Apply to client</button>
              {!p.system && <button data-testid={`prog-delete-${p.id}`} onClick={() => removeProgram(p)} className="px-3 py-2 border border-white/15 rounded-lg text-tertiary hover:text-brand"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
      </div>

      {/* Apply modal */}
      {apply && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-50" onClick={() => setApply(null)}>
          <div className="card-surface rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl">Apply Program</h2>
            <p className="text-secondary text-sm mt-1">{apply.program.name} · {apply.program.weeks} weeks</p>
            <label className="label-xs block mt-4 mb-1">Client</label>
            <select data-testid="apply-customer" value={apply.customer_id} onChange={(e) => setApply({ ...apply, customer_id: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand">
              <option value="">{customers.length ? "Select a client…" : "No clients yet"}</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="label-xs block mt-4 mb-1">Start date</label>
            <input data-testid="apply-start" type="date" value={apply.start} onChange={(e) => setApply({ ...apply, start: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
            <p className="text-tertiary text-xs mt-2">The program's weeks anchor to the Monday of this week; days before the start date are skipped.</p>
            <button data-testid="apply-confirm" onClick={doApply} className="w-full mt-5 py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover">Apply Program</button>
          </div>
        </div>
      )}

      {/* Builder modal */}
      {builder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-50" onClick={() => setBuilder(null)}>
          <div className="card-surface rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-heading text-3xl">New Program</h2><button onClick={() => setBuilder(null)} className="text-tertiary hover:text-white"><X className="w-5 h-5" /></button></div>
            <p className="text-secondary text-sm mt-1">Design one week — it repeats for the number of weeks you choose.</p>
            <label className="label-xs block mt-4 mb-1">Name</label>
            <input data-testid="prog-name" value={builder.name} onChange={(e) => setBuilder({ ...builder, name: e.target.value })} placeholder="e.g. 6-Week Strength Base" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
            <label className="label-xs block mt-3 mb-1">Description</label>
            <input data-testid="prog-desc" value={builder.description} onChange={(e) => setBuilder({ ...builder, description: e.target.value })} placeholder="Short summary" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
            <label className="label-xs block mt-3 mb-1">Repeat for</label>
            <div className="flex items-center gap-2"><input data-testid="prog-weeks" type="number" min="1" max="52" value={builder.weeks} onChange={(e) => setBuilder({ ...builder, weeks: e.target.value })} className="w-20 bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-center" /><span className="text-sm text-secondary">week(s)</span></div>

            <label className="label-xs block mt-4 mb-2">Weekly plan</label>
            {templates.length === 0 && <p className="text-tertiary text-xs mb-2">No workout templates yet — build & save workouts in the Workout Builder first.</p>}
            <div className="space-y-1.5">
              {WEEKDAYS.map(([lbl, wd]) => (
                <div key={wd} className="flex items-center gap-2">
                  <span className="text-sm w-10">{lbl}</span>
                  <select data-testid={`prog-day-${wd}`} value={builder.days[wd] || ""} onChange={(e) => setBuilder({ ...builder, days: { ...builder.days, [wd]: e.target.value } })} className="flex-1 bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand">
                    <option value="">— off —</option>
                    <option value="rest">Rest day</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <button data-testid="prog-save" onClick={saveBuilder} className="w-full mt-5 py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover">Save Program</button>
          </div>
        </div>
      )}
    </div>
  );
}
